import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import {
  Newspaper, Filter, FileText,
  Loader2, ExternalLink, Globe, Building2,
  Leaf, HeartPulse, Scale, Landmark, Lightbulb, TrendingUp
} from "lucide-react";

const TOPICS = [
  { id: "economy", label: "Economy", icon: TrendingUp, color: "bg-green-100 text-green-700" },
  { id: "polity", label: "Polity", icon: Landmark, color: "bg-blue-100 text-blue-700" },
  { id: "international", label: "International", icon: Globe, color: "bg-purple-100 text-purple-700" },
  { id: "environment", label: "Environment", icon: Leaf, color: "bg-emerald-100 text-emerald-700" },
  { id: "science_technology", label: "Science & Tech", icon: Lightbulb, color: "bg-amber-100 text-amber-700" },
  { id: "health", label: "Health", icon: HeartPulse, color: "bg-red-100 text-red-700" },
  { id: "governance", label: "Governance", icon: Building2, color: "bg-slate-100 text-slate-700" },
  { id: "social", label: "Social Issues", icon: Scale, color: "bg-pink-100 text-pink-700" },
];

const GS_PAPERS = [
  { id: "GS1", label: "GS Paper 1", desc: "History, Geography, Society" },
  { id: "GS2", label: "GS Paper 2", desc: "Polity, Governance, IR" },
  { id: "GS3", label: "GS Paper 3", desc: "Economy, Environment, S&T" },
  { id: "GS4", label: "GS Paper 4", desc: "Ethics & Integrity" },
];

const NewsHubPage = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState("topic");
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [topicNews, setTopicNews] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("economy");
  const [gsPaperNews, setGsPaperNews] = useState([]);
  const [selectedGS, setSelectedGS] = useState("GS2");

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
    }
  }, [user, token, navigate]);

  const fetchWithAuth = async (endpoint) => {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  };

  // Fetch Topic News
  const loadTopicNews = async (topic) => {
    setLoading(true);
    setSelectedTopic(topic);
    try {
      const data = await fetchWithAuth(`/ca-magazine/topic-news/${topic}?days=14`);
      setTopicNews(data.items || []);
    } catch (error) {
      toast({ variant: "destructive", description: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Fetch GS Paper News
  const loadGSPaperNews = async (gs) => {
    setLoading(true);
    setSelectedGS(gs);
    try {
      const data = await fetchWithAuth(`/ca-magazine/gs-paper-news/${gs}?days=14`);
      setGsPaperNews(data.items || []);
    } catch (error) {
      toast({ variant: "destructive", description: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Load data on tab change
  useEffect(() => {
    if (!token) return;
    
    switch (activeTab) {
      case "topic":
        if (topicNews.length === 0) loadTopicNews(selectedTopic);
        break;
      case "gs":
        if (gsPaperNews.length === 0) loadGSPaperNews(selectedGS);
        break;
    }
  }, [activeTab, token]);

  const NewsCard = ({ item, showSource = true }) => (
    <Card 
      className="hover:shadow-md transition-all cursor-pointer group border-slate-200"
      onClick={() => item.url && window.open(item.url, '_blank')}
      data-testid="news-card"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
            <Newspaper className="h-4 w-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-800 text-sm line-clamp-2 group-hover:text-orange-600 transition-colors">
              {item.title || item.topic_hint}
            </h4>
            {item.context_hint && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.context_hint}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {showSource && item.source && (
                <Badge variant="outline" className="text-xs">{item.source}</Badge>
              )}
              {item.topics?.map((t, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
              ))}
            </div>
          </div>
          {item.url && (
            <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-orange-500 flex-shrink-0" />
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50/30 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg">
              <Newspaper className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">News Hub</h1>
              <p className="text-slate-500 text-sm">Scraped from 30+ official sources</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-white/80 border border-slate-200">
            <TabsTrigger value="topic" className="flex items-center gap-2" data-testid="tab-topic">
              <Filter className="h-4 w-4" />
              <span>Topic News</span>
            </TabsTrigger>
            <TabsTrigger value="gs" className="flex items-center gap-2" data-testid="tab-gs">
              <FileText className="h-4 w-4" />
              <span>GS Papers</span>
            </TabsTrigger>
          </TabsList>

          {/* Topic News Tab */}
          <TabsContent value="topic">
            <Card className="bg-white/80 border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-orange-500" />
                  News by Topic
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Topic Selector */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {TOPICS.map((topic) => (
                    <Button
                      key={topic.id}
                      variant={selectedTopic === topic.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => loadTopicNews(topic.id)}
                      className={selectedTopic === topic.id ? "bg-orange-500 hover:bg-orange-600" : ""}
                      data-testid={`topic-btn-${topic.id}`}
                    >
                      <topic.icon className="h-4 w-4 mr-1" />
                      {topic.label}
                    </Button>
                  ))}
                </div>

                {/* News List */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : topicNews.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {topicNews.map((item, i) => (
                      <NewsCard key={i} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Filter className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No news found for {selectedTopic}. Try another topic.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GS Paper News Tab */}
          <TabsContent value="gs">
            <Card className="bg-white/80 border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-orange-500" />
                  News by GS Paper
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* GS Paper Selector */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {GS_PAPERS.map((gs) => (
                    <Button
                      key={gs.id}
                      variant={selectedGS === gs.id ? "default" : "outline"}
                      className={`h-auto py-3 flex-col ${selectedGS === gs.id ? "bg-orange-500 hover:bg-orange-600" : ""}`}
                      onClick={() => loadGSPaperNews(gs.id)}
                      data-testid={`gs-btn-${gs.id}`}
                    >
                      <span className="font-bold">{gs.label}</span>
                      <span className="text-xs opacity-80">{gs.desc}</span>
                    </Button>
                  ))}
                </div>

                {/* News List */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : gsPaperNews.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {gsPaperNews.map((item, i) => (
                      <NewsCard key={i} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No news found for {selectedGS}. Try another paper.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NewsHubPage;
